/**
 * Orbs — la moneda virtual que ve el usuario en speak.
 *
 * Es una CAPA DE PRESENTACIÓN sobre el ledger en nano-USD. El ledger interno
 * (`users.credits_balance_nano`, `credit_transactions`) y TODO el billing/cost/
 * markup/reconcile/admin siguen 100% en nano-USD — orbs nunca toca esos
 * números. Sólo cambia lo que se MUESTRA dentro de la app (saldo, consumo,
 * packs) y en los emails al usuario.
 *
 * Tasa fija (decisión del dueño 2026-06-30): **1 orb = $0,001 USD = 100_000
 * nano** (1 USD = 1e8 nano). `NANO_PER_ORB` es la ÚNICA fuente de verdad de la
 * tasa: tanto el backend (emails) como `apps/speak` (todo el display) la
 * importan de acá. Cambiar la tasa = cambiar SÓLO esta constante.
 *
 * La tasa es COSMÉTICA: escala el número que ve el user, no la economía. El
 * margen sale del markup de uso (`billing_config.markupBps`) y del mapeo
 * SKU→nano de los packs, NO de esta constante. Ver
 * `docs/architecture/05-providers-and-billing.md`.
 *
 * Vive en `@orborix/shared-types` (sólo depende de zod) a propósito: es el
 * único paquete que el frontend de speak puede importar sin arrastrar node/pg
 * al bundle, y el backend también lo importa → una sola fuente para los dos.
 */

/** nano-USD por orb. 1 orb = $0,001 = 100_000 nano (1 USD = 1e8 nano). */
export const NANO_PER_ORB = 100_000n;

/**
 * Convierte un monto en nano-USD a orbs (entero, redondeo half-up sobre el
 * valor absoluto, preservando el signo). Devuelve `number`: los saldos reales
 * (hasta unos millones de orbs) entran sobrados en el rango seguro de JS
 * (2^53). Acepta string para los montos que viajan BigInt-safe por la API.
 */
export function nanoToOrbs(nano: bigint | string): number {
  const n = typeof nano === 'string' ? BigInt(nano) : nano;
  const negative = n < 0n;
  const abs = negative ? -n : n;
  // half-up: sumamos medio orb antes de truncar con división entera.
  const orbs = (abs + NANO_PER_ORB / 2n) / NANO_PER_ORB;
  // Un overdraft chico (ej. -$0,0004) redondea a 0 orbs: normalizamos a +0 para
  // no mostrar el contradictorio "-0 orbs" (Intl emite el signo del -0).
  if (orbs === 0n) return 0;
  return negative ? -Number(orbs) : Number(orbs);
}

/**
 * orbs (entero) → nano-USD exacto. Para mapear packs (SKU→orbs) y tests sin
 * drift de coma flotante.
 */
export function orbsToNano(orbs: number): bigint {
  if (!Number.isInteger(orbs)) {
    throw new RangeError(`orbsToNano: orbs must be an integer (got ${orbs})`);
  }
  return BigInt(orbs) * NANO_PER_ORB;
}

/**
 * Formatea un monto nano-USD como orbs agrupados, SIN la unidad
 * (ej. 1_000_000_000n → "10.000" en es-AR). El sufijo "orbs"/"orb" lo agrega
 * el call site con `formatOrbsLabel` o a mano, porque la pluralización y el
 * idioma varían por contexto. `locale` controla el separador de miles
 * (es-AR → "10.000", en → "10,000").
 */
export function formatOrbs(nano: bigint | string, locale = 'es-AR'): string {
  return nanoToOrbs(nano).toLocaleString(locale, { maximumFractionDigits: 0 });
}

/**
 * Etiqueta lista para mostrar con unidad y pluralización: "4.990 orbs",
 * "1 orb", "0 orbs". El signo (±) NO se agrega acá — eso lo decide el call
 * site según el contexto (un débito muestra "−", el saldo no muestra signo).
 */
export function formatOrbsLabel(nano: bigint | string, locale = 'es-AR'): string {
  const orbs = nanoToOrbs(nano);
  const unit = Math.abs(orbs) === 1 ? 'orb' : 'orbs';
  return `${formatOrbs(nano, locale)} ${unit}`;
}

/**
 * Como `formatOrbs`, pero pensado para montos de COSTO (desglose de sesión,
 * por-turno): un costo positivo que redondea a 0 orbs (un servicio que costó
 * una fracción de orb) se muestra como **"<1"** en vez de "0" — que se leería
 * como gratis/roto. El 0 exacto (no se usó ese servicio) sigue siendo "0".
 */
export function formatOrbsCost(nano: bigint | string, locale = 'es-AR'): string {
  const n = typeof nano === 'string' ? BigInt(nano) : nano;
  if (n > 0n && nanoToOrbs(n) === 0) return '<1';
  return formatOrbs(n, locale);
}
